import type { FormProps } from 'antd'
import { Col, Form, Input, Modal, Row, Select } from 'antd'
import { useEffect, useMemo } from 'react'
import { categories } from '../model/data/categories'
import { countries } from '../model/data/countries'
import { languages } from '../model/data/languages'
import { useChannels } from '../model/hooks/use-channels'
import { InternetRecourceType, type Channel } from '../model/types'

interface EditChannelModalProps {
    open: boolean
    channel: Channel
    onClose: () => void
}

type FieldType = {
    username: string
    language: string
    country: string
    category: string
    timezone: string
    url: string
    name: string
}

export const EditChannelModal = ({ open, channel, onClose }: EditChannelModalProps) => {
    const {
        actions: { editChannel },
        editChannelIsLoading
    } = useChannels()

    const timezones = useMemo(() => Intl.supportedValuesOf('timeZone'), [])

    const onFinish: FormProps<FieldType>['onFinish'] = async ({ timezone, category, country, language, url, name }) => {
        await editChannel({ id: channel.id, data: { timezone, category, country, language, url, name } })
        onClose()
    }

    const [form] = Form.useForm()

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    useEffect(() => {
        form.setFieldsValue({
            username: channel.username,
            timezone: channel.timezone,
            category: channel.category,
            country: channel.country,
            language: channel.language,
            url: channel.url,
            name: channel.name
        })
    }, [channel, form])

    const isSelectedResourceChannel = useMemo(() => channel.type === InternetRecourceType.channel, [channel])

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            centered={false}
            title="Редактирование канала."
            okText="Сохранить"
            okButtonProps={{
                loading: editChannelIsLoading
            }}
            cancelText="Отменить"
        >
            <Form form={form} name="edit-channel" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} onFinish={onFinish}>
                <Row gutter={[16, 16]}>
                    {isSelectedResourceChannel ? (
                        <Col span={24}>
                            <Form.Item<FieldType>
                                label="Юзернейм канала"
                                name="username"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item<FieldType>
                                label="Выберите категорию канала"
                                name="category"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Select showSearch style={{ width: '100%' }} placeholder="Выберите категорию канала">
                                    {categories.map((c: { value: string; label: string }) => (
                                        <Select.Option key={c.value}>{c.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<FieldType>
                                label="Выберите страну канала"
                                name="country"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Select showSearch style={{ width: '100%' }} placeholder="Выберите страну канала">
                                    {countries.map((c: { value: string; label: string }) => (
                                        <Select.Option key={c.value}>{c.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<FieldType>
                                label="Выберите язык канала"
                                name="language"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Select showSearch style={{ width: '100%' }} placeholder="Выберите язык канала">
                                    {languages.map((c: { value: string; label: string }) => (
                                        <Select.Option key={c.value}>{c.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<FieldType>
                                label="Укажите часовой пояс канала"
                                name="timezone"
                                required={false}
                                initialValue={Intl.DateTimeFormat().resolvedOptions().timeZone}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Select showSearch style={{ width: '100%' }} placeholder="Выберите часовой пояс">
                                    {timezones.map((tz: string) => (
                                        <Select.Option key={tz}>{tz}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    ) : (
                        <>
                            <Col span={24}>
                                <Form.Item<FieldType>
                                    label="Введите название сайта"
                                    name="name"
                                    required={false}
                                    rules={[{ required: !isSelectedResourceChannel, message: 'Обязательное поле' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item<FieldType>
                                    label="Введите ссылку на сайт"
                                    name="url"
                                    required={false}
                                    rules={[{ required: !isSelectedResourceChannel, message: 'Обязательное поле' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        </Modal>
    )
}
